import { defineResource, deriveResource } from 'pulse/resources';
import { defaultPropertyGroupsResolver } from './resolver';
import { propertiesPersistedPromise } from 'fdsr-migration-layer';
export const RootPropertyGroups = defineResource({
  typeName: 'hs-resources:RootPropertyGroups',
  resolver: defaultPropertyGroupsResolver,
  args: ['objectTypeId']
}).onChange(({
  state,
  params,
  dataChanged
}) => {
  if (state.data && dataChanged) {
    propertiesPersistedPromise.syncIntoCache(Promise.resolve(state.data), {
      frameworkTypeIdentifier: params.objectTypeId,
      query: {
        showHighlySensitiveProperties: true
      },
      // HACK: This isn't used for the cache key, just an unfortunate part of the api
      httpClient: {}
    });
  }
}).register();

// If showHSP is false, we need to mark any property with a dataSensitivity of "high" as hidden: true.
// We always fetch with showHighlySensitiveProperties: true because we can do this derivation.
// This logic originally lived in FDSR: https://github.com/HubSpotEngineering/framework-data-schema-resolvers/blob/master/framework-data-schema-resolvers/static/js/properties/makePropertiesClient.ts#L177-L224
// It **must** mirror the logic in the BE: https://git.hubteam.com/HubSpotProtected/InboundDbProperties/blob/3a9dbb099b1ed16a2bdf16b9b010cf8b0db42207/InboundDbPropertiesService/src/main/java/com/hubspot/inbounddb/properties/service/helpers/PropertiesResponseTransform.java#L56-L72
const PropertyGroupsHSPTransform = deriveResource('hs-resources:PropertyGroupsHSPTransform').from(RootPropertyGroups).mapParams(params => ({
  objectTypeId: params.objectTypeId
})).mergeArgs([{
  field: 'showHighlySensitiveProperties',
  default: false
}]).pull(({
  self,
  other
}) => {
  const originalHiddenValues = {};
  const groups = [];

  // Return as-is if the caller wants to see HSPs
  if (self.params.showHighlySensitiveProperties) {
    return {
      groups: other.data
    };
  }
  for (const group of other.data) {
    if (!group.propertyDefinitions) {
      groups.push(group);
      continue;
    }
    const propertyDefinitions = group.propertyDefinitions.slice();
    for (let i = 0; i < propertyDefinitions.length; i++) {
      const def = propertyDefinitions[i];
      if (def.property.dataSensitivity === 'high') {
        originalHiddenValues[def.property.name] = def.property.hidden;
        propertyDefinitions[i] = Object.assign({}, def, {
          property: Object.assign({}, def.property, {
            hidden: true
          })
        });
      }
    }
    groups.push(Object.assign({}, group, {
      propertyDefinitions
    }));
  }
  return {
    originalHiddenValues,
    groups
  };
}).push(({
  self
}) => {
  if (self.params.showHighlySensitiveProperties) {
    return self.data.groups;
  }

  // Restore original hidden values
  return self.data.groups.map(group => {
    var _group$propertyDefini;
    return Object.assign({}, group, {
      propertyDefinitions: (_group$propertyDefini = group.propertyDefinitions) === null || _group$propertyDefini === void 0 ? void 0 : _group$propertyDefini.map(definition => {
        var _self$data$originalHi, _self$data$originalHi2;
        return Object.assign({}, definition, {
          property: Object.assign({}, definition.property, {
            hidden: (_self$data$originalHi = (_self$data$originalHi2 = self.data.originalHiddenValues) === null || _self$data$originalHi2 === void 0 ? void 0 : _self$data$originalHi2[definition.property.name]) !== null && _self$data$originalHi !== void 0 ? _self$data$originalHi : definition.property.hidden
          })
        });
      })
    });
  });
}).register();
export const PropertyGroups = deriveResource('hs-resources:PropertyGroups').from(PropertyGroupsHSPTransform).pull(({
  other
}) => other.data.groups).push(({
  self,
  other
}) => other.data && Object.assign({}, other.data, {
  groups: self.data
})).register();